// Break-progress crack overlay (UI-4). A unit box drawn over the block being
// mined, its texture stepping through 10 procedurally-drawn crack stages as the
// break progresses (client/render/overlay.ts per the module tree). Client-only
// (ENG-1). No assets — cracks are painted to canvases.
import * as THREE from 'three';

const STAGES = 10;

/** The block currently being mined + fractional progress (0..1). */
export interface MiningState {
  active: boolean;
  x: number;
  y: number;
  z: number;
  progress: number;
}

function crackTexture(stage: number): THREE.Texture {
  const S = 16;
  const cvs = document.createElement('canvas');
  cvs.width = S; cvs.height = S;
  const ctx = cvs.getContext('2d')!;
  ctx.clearRect(0, 0, S, S);
  // Deterministic crack lines that accumulate with the stage.
  let seed = 1234;
  const rnd = (): number => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const lines = (stage + 1) * 2;
  ctx.strokeStyle = 'rgba(0,0,0,0.85)';
  ctx.lineWidth = 1;
  for (let i = 0; i < lines; i++) {
    const x0 = Math.floor(rnd() * S), y0 = Math.floor(rnd() * S);
    const x1 = x0 + Math.floor((rnd() - 0.5) * (4 + stage));
    const y1 = y0 + Math.floor((rnd() - 0.5) * (4 + stage));
    ctx.beginPath();
    ctx.moveTo(x0 + 0.5, y0 + 0.5);
    ctx.lineTo(x1 + 0.5, y1 + 0.5);
    ctx.stroke();
  }
  const tex = new THREE.CanvasTexture(cvs);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  return tex;
}

export class BreakOverlay {
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private readonly textures: THREE.Texture[];
  private stage = -1;

  constructor(scene: THREE.Scene) {
    this.textures = Array.from({ length: STAGES }, (_, i) => crackTexture(i));
    this.material = new THREE.MeshBasicMaterial({
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
    });
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(1.004, 1.004, 1.004), this.material);
    this.mesh.visible = false;
    this.mesh.renderOrder = 2;
    scene.add(this.mesh);
  }

  update(m: MiningState): void {
    if (!m.active) { this.mesh.visible = false; this.stage = -1; return; }
    this.mesh.visible = true;
    this.mesh.position.set(m.x + 0.5, m.y + 0.5, m.z + 0.5);
    const stage = Math.max(0, Math.min(STAGES - 1, Math.floor(m.progress * STAGES)));
    if (stage !== this.stage) {
      this.material.map = this.textures[stage]!;
      this.material.needsUpdate = true;
      this.stage = stage;
    }
  }
}
